import React, { useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2 } from 'lucide-react';
import { Seo } from '@/components/common/Seo';
import { PageHero } from '@/components/common/PageHero';
import { OphirButton } from '@/components/common/OphirButton';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { submitBuyer } from '@/lib/api';
import { useContent } from '@/context/ContentContext';

const labelCls = 'text-[12px] font-semibold uppercase tracking-[0.1em] text-ophir-navy';

export default function Buyers() {
  const { content } = useContent();
  const seo = content?.seo?.buyers;
  const opts = content?.form_options || {};
  const cats = opts.product_categories || [];
  const [form, setForm] = useState({
    company_name: '', business_type: '', website: '', contact_name: '', business_email: '',
    telephone: '', country: '', province_state: '', product_categories: [], purchasing_requirements: '',
    delivery_region: '', message: '', consent: false, company_website_hp: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleCat = (c) => set('product_categories', form.product_categories.includes(c) ? form.product_categories.filter((x) => x !== c) : [...form.product_categories, c]);

  const validate = () => {
    const e = {};
    if (!form.company_name.trim()) e.company_name = 'Required';
    if (!form.contact_name.trim()) e.contact_name = 'Required';
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.business_email)) e.business_email = 'Enter a valid email';
    if (!form.country.trim()) e.country = 'Required';
    if (!form.consent) e.consent = 'Consent is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) { toast.error('Please complete the required fields.'); return; }
    setSubmitting(true);
    try {
      await submitBuyer(form);
      setDone(true); window.scrollTo(0, 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Something went wrong.');
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <>
        <Seo title={seo?.title} description={seo?.description} />
        <PageHero label="For buyers" title="Inquiry received" />
        <section className="bg-white"><div className="container-ophir py-16">
          <div data-testid="buyer-success" className="max-w-xl bg-ophir-ivory-soft border border-ophir-grey rounded-2xl p-8 shadow-ophir">
            <CheckCircle2 className="text-ophir-navy" size={40} />
            <h2 className="mt-4 font-serif text-[24px] text-ophir-navy-deep">Thank you for your inquiry</h2>
            <p className="mt-3 text-[15.5px] leading-relaxed text-ophir-muted">Our team will review your purchasing requirements and respond regarding suitable product categories and commercial supply opportunities.</p>
            <div className="mt-6"><OphirButton to="/">Return Home</OphirButton></div>
          </div>
        </div></section>
      </>
    );
  }

  return (
    <>
      <Seo title={seo?.title} description={seo?.description} />
      <PageHero label="For buyers" title="For retailers and commercial buyers"
        intro="Tell us about your purchasing requirements. We will discuss suitable product categories, delivery regions and commercial supply opportunities."
        image="/images/ophir/buyers/planning.webp"
        imageAlt="A small retail procurement team reviewing an assortment of products, a product list and delivery notes" />

      <section className="bg-white">
        <div className="container-ophir py-16 lg:py-24">
          <form data-testid="buyer-inquiry-form" onSubmit={onSubmit} noValidate className="max-w-3xl space-y-6">
            <input type="text" tabIndex={-1} autoComplete="off" className="hidden" value={form.company_website_hp} onChange={(e) => set('company_website_hp', e.target.value)} aria-hidden="true" />
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label className={labelCls}>Company name *</Label>
                <Input data-testid="buyer-company" value={form.company_name} onChange={(e) => set('company_name', e.target.value)} className="mt-2" />
                {errors.company_name && <p className="mt-1 text-[13px] text-[#B42318]">{errors.company_name}</p>}
              </div>
              <div>
                <Label className={labelCls}>Business type</Label>
                <Select value={form.business_type} onValueChange={(v) => set('business_type', v)}>
                  <SelectTrigger data-testid="buyer-business-type" className="mt-2"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{(opts.business_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className={labelCls}>Website</Label>
                <Input data-testid="buyer-website" value={form.website} onChange={(e) => set('website', e.target.value)} className="mt-2" placeholder="https://" />
              </div>
              <div>
                <Label className={labelCls}>Contact name *</Label>
                <Input data-testid="buyer-contact-name" value={form.contact_name} onChange={(e) => set('contact_name', e.target.value)} className="mt-2" />
                {errors.contact_name && <p className="mt-1 text-[13px] text-[#B42318]">{errors.contact_name}</p>}
              </div>
              <div>
                <Label className={labelCls}>Business email *</Label>
                <Input type="email" data-testid="buyer-email" value={form.business_email} onChange={(e) => set('business_email', e.target.value)} className="mt-2" />
                {errors.business_email && <p className="mt-1 text-[13px] text-[#B42318]">{errors.business_email}</p>}
              </div>
              <div>
                <Label className={labelCls}>Telephone</Label>
                <Input data-testid="buyer-telephone" value={form.telephone} onChange={(e) => set('telephone', e.target.value)} className="mt-2" />
              </div>
              <div>
                <Label className={labelCls}>Country *</Label>
                <Input data-testid="buyer-country" value={form.country} onChange={(e) => set('country', e.target.value)} className="mt-2" />
                {errors.country && <p className="mt-1 text-[13px] text-[#B42318]">{errors.country}</p>}
              </div>
              <div>
                <Label className={labelCls}>Province / state</Label>
                <Input data-testid="buyer-province" value={form.province_state} onChange={(e) => set('province_state', e.target.value)} className="mt-2" />
              </div>
            </div>

            <div>
              <Label className={labelCls}>Product categories of interest</Label>
              <div className="mt-3 grid sm:grid-cols-2 gap-2">
                {cats.map((c) => (
                  <label key={c} className="flex items-center gap-3 rounded-lg border border-ophir-grey bg-ophir-ivory-soft px-4 py-3 cursor-pointer">
                    <Checkbox checked={form.product_categories.includes(c)} onCheckedChange={() => toggleCat(c)} />
                    <span className="text-[14.5px] text-ophir-navy">{c}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label className={labelCls}>Estimated purchasing requirements</Label>
                <Input data-testid="buyer-requirements" value={form.purchasing_requirements} onChange={(e) => set('purchasing_requirements', e.target.value)} className="mt-2" placeholder="e.g. volumes, frequency" />
              </div>
              <div>
                <Label className={labelCls}>Delivery region</Label>
                <Select value={form.delivery_region} onValueChange={(v) => set('delivery_region', v)}>
                  <SelectTrigger data-testid="buyer-delivery-region" className="mt-2"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{(opts.territories || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className={labelCls}>Message</Label>
              <Textarea data-testid="buyer-message" rows={5} value={form.message} onChange={(e) => set('message', e.target.value)} className="mt-2" />
            </div>

            <div className="flex items-start gap-3">
              <Checkbox id="b-consent" data-testid="buyer-consent" checked={form.consent} onCheckedChange={(v) => set('consent', !!v)} className="mt-1" />
              <Label htmlFor="b-consent" className="text-[14px] font-normal leading-relaxed text-ophir-muted">I consent to OPHIR SUPPLY storing and using this information to respond to my inquiry. *</Label>
            </div>
            {errors.consent && <p className="text-[13px] text-[#B42318]">{errors.consent}</p>}

            <OphirButton type="submit" data-testid="buyer-submit" disabled={submitting}>{submitting ? 'Submitting…' : 'Make a Buyer Inquiry'}</OphirButton>
          </form>
        </div>
      </section>
    </>
  );
}
